import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Workflow } from "@/types/aiToolkit";
import { aiService, type OpenRouterModel as AIServiceModel } from "@/services/aiService";
import * as githubService from "@/services/githubService";
import { getRepoIndexStatus, getRepoTreePathsFromIndex, type RepoIndexStatus } from "@/services/repoRagService";
import { embeddingJobService, deriveLiveRepoIndexStatus } from "@/services/embeddingJobService";
import {
  getChatPreferencesSnapshot,
  isChatPreferencesHydrated,
  subscribeChatPreferences,
  updateChatPreferences,
} from "@/lib/chatPreferencesStore";
import { resolveOpenRouterModelId } from "@/lib/openRouterModelAliases";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useAuth } from "@/contexts/AuthContext";

type SelectedSuperPrompt = {
  id: string;
  name: string;
  description: string;
  promptTemplate: string;
  category: string;
  icon: string;
  color: string;
  tags: string[];
  isSystem: boolean;
  isFavorite?: boolean;
};

type SelectedGitHubRepo = { owner: string; name: string; fullName: string };
const MAX_ATTACHED_REPOS = 5;
const MODELS_CACHE_KEY = "models_cache";

function loadModelsCache(): AIServiceModel[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(MODELS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((m): m is AIServiceModel => Boolean(m && typeof (m as AIServiceModel).id === "string"));
  } catch {
    return [];
  }
}

export type RepoIndexingState = {
  status: 'idle' | 'indexing' | 'done' | 'error';
  progress: number;
  batchIndex?: number;
  totalBatches?: number;
  chunksIndexed?: number;
  errorMessage?: string;
  phase?: string;
  lastHeartbeat?: string;
  indexStatus?: RepoIndexStatus;
  symbolsStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
};

type ThreadData = {
  id?: string;
  agentId?: string | null;
  contextMode?: string;
  chatMode?: string;
  linkedSpaceIds?: string[];
  attachedGitHubRepos?: SelectedGitHubRepo[];
  topicId?: string | null;
} | null | undefined;

type UpdateThreadMutation = {
  mutate: (
    args: {
      id: string;
      data: Partial<{
        contextMode: string;
        chatMode: string;
        agentId?: string | null;
        attachedGitHubRepos?: SelectedGitHubRepo[];
      }>;
    },
    options?: {
      onSuccess?: () => void;
      onError?: () => void;
    }
  ) => void;
};

export interface UseChatToolsOptions {
  currentThreadId: string | null;
  currentThread: ThreadData;
  updateThreadMutation: UpdateThreadMutation;
}

export function useChatTools({
  currentThreadId,
  currentThread,
  updateThreadMutation,
}: UseChatToolsOptions) {
  const { user } = useAuth();
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);

  const [selectedModel, setSelectedModel] = useState(() => {
    const saved = getChatPreferencesSnapshot().preferredModel;
    return saved ? resolveOpenRouterModelId(saved) : "";
  });
  const [modelInfo, setModelInfo] = useState<AIServiceModel | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedSuperPrompt, setSelectedSuperPrompt] = useState<SelectedSuperPrompt | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [webSearchMode, setWebSearchMode] = useState<"open" | "closed">(
    () => getChatPreferencesSnapshot().webSearchMode
  );
  const [thinkingMode, setThinkingMode] = useState(() => getChatPreferencesSnapshot().thinkingMode);
  const [autoSelectBest, setAutoSelectBest] = useState(() => getChatPreferencesSnapshot().autoSelectBest);
  const [deepResearchMode, setDeepResearchMode] = useState(false);
  const [contextMode, setContextMode] = useState<"MAX" | "LITE">(
    () => getChatPreferencesSnapshot().contextMode
  );
  const [chatMode, setChatMode] = useState<"normal" | "questioning" | "dialogue">(
    () => getChatPreferencesSnapshot().chatMode
  );
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [useFullScope, setUseFullScope] = useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  const [selectedGitHubRepos, setSelectedGitHubRepos] = useState<SelectedGitHubRepo[]>([]);
  const selectedGitHubRepo = selectedGitHubRepos[0] ?? null;
  const setSelectedGitHubRepo = useCallback((repo: SelectedGitHubRepo | null) => {
    setSelectedGitHubRepos(repo ? [repo] : []);
  }, []);
  const [cachedRepoContexts, setCachedRepoContexts] = useState<
    Record<string, Awaited<ReturnType<typeof githubService.getRepoAnalysisContext>>>
  >({});
  const cachedRepoContext = selectedGitHubRepo
    ? cachedRepoContexts[selectedGitHubRepo.fullName] ?? null
    : null;
  const cachedRepoContextRef = useRef(cachedRepoContexts);
  cachedRepoContextRef.current = cachedRepoContexts;
  const [isLoadingRepoContext, setIsLoadingRepoContext] = useState(false);
  const [repoIndexingProgressMap, setRepoIndexingProgressMap] = useState<
    Record<string, RepoIndexingState>
  >({});
  const repoIndexingProgress = selectedGitHubRepo
    ? repoIndexingProgressMap[selectedGitHubRepo.fullName] ?? { status: 'idle' as const, progress: 0 }
    : { status: 'idle' as const, progress: 0 };
  const lastFetchedReposRef = useRef<Set<string>>(new Set());
  const maxProgressSeenRef = useRef<Map<string, number>>(new Map());
  const indexingInFlightRef = useRef<Map<string, Promise<void>>>(new Map());
  const indexingAbortRef = useRef<Map<string, AbortController>>(new Map());

  const selectedReposKey = useMemo(
    () => selectedGitHubRepos.map((r) => r.fullName).sort().join('|'),
    [selectedGitHubRepos]
  );

  const lastSavedAgentId = useRef<string | null>(null);
  const lastSavedGitHubReposRef = useRef<string | null>(null);
  const pendingThreadUpdateRef = useRef<Partial<{
    contextMode: string;
    chatMode: string;
    agentId?: string | null;
    attachedGitHubRepos?: SelectedGitHubRepo[];
  }>>({});
  const pendingThreadUpdateTimerRef = useRef<number | null>(null);
  const pendingThreadIdRef = useRef<string | null>(null);
  const lastLoadedToolsThreadIdRef = useRef<string | null>(null);
  const lastPersistedThreadSettingsRef = useRef<string | null>(null);

  // Fetch available models from OpenRouter using client-side service
  const [modelsLastUpdated, setModelsLastUpdated] = useState<Date | null>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("models_last_updated");
      return saved ? new Date(saved) : null;
    }
    return null;
  });

  const {
    data: availableModels = [],
    refetch: refetchModels,
    isFetching: isRefreshingModels,
    isLoading: isLoadingModels,
    isError: isModelsFetchError,
    error: modelsFetchError,
    isFetched: isModelsFetched,
  } = useQuery<AIServiceModel[]>({
    queryKey: ["/api/models"],
    queryFn: async () => {
      const models = await aiService.fetchModels();
      const now = new Date();
      setModelsLastUpdated(now);
      localStorage.setItem("models_last_updated", now.toISOString());
      localStorage.setItem(MODELS_CACHE_KEY, JSON.stringify(models));
      return models as AIServiceModel[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 60 * 24,
    retry: 2,
    retryDelay: 1000,
    placeholderData: loadModelsCache,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!user) return;
    if (availableModels.length === 0 && !isRefreshingModels && !isModelsFetchError) {
      refetchModels();
    }
  }, [user, availableModels.length, isRefreshingModels, isModelsFetchError, refetchModels]);

  // Set default model only if user has no saved preference; reset if saved model is no longer available (e.g. audio-only filtered out)
  useEffect(() => {
    if (availableModels && availableModels?.length > 0) {
      const savedModel = getChatPreferencesSnapshot().preferredModel;
      const resolvedSaved = savedModel ? resolveOpenRouterModelId(savedModel) : null;
      if (resolvedSaved && resolvedSaved !== savedModel) {
        updateChatPreferences({ preferredModel: resolvedSaved });
        setSelectedModel(resolvedSaved);
      }
      if (resolvedSaved && availableModels.some((m) => m.id === resolvedSaved)) {
        const savedModelInfo = availableModels.find((m) => m.id === resolvedSaved);
        if (savedModelInfo) {
          setModelInfo(savedModelInfo);
        }
        return;
      }
      if (selectedModel && !availableModels.some((m) => m.id === selectedModel)) {
        const fallback = availableModels.find((m) => m.id.includes("openai/gpt-4o")) ?? availableModels[0];
        if (fallback) {
          setSelectedModel(fallback.id);
          setModelInfo(fallback);
        }
        return;
      }
      if (!selectedModel) {
        const openAI = availableModels?.filter((newModel: any) =>
          newModel?.id?.includes("openai/")
        );
        if (openAI && openAI[0]) {
          setSelectedModel(openAI[0]?.id);
          setModelInfo(openAI[0]);
        }
      }
    }
  }, [availableModels, selectedModel]);

  // Persist tool settings to the user's Supabase-backed chat preferences.
  // Skips until hydration completes so a pre-hydration default can't clobber the stored value.
  useEffect(() => {
    if (!isChatPreferencesHydrated()) return;
    const prefs = getChatPreferencesSnapshot();
    const update: Parameters<typeof updateChatPreferences>[0] = {};
    if (selectedModel && prefs.preferredModel !== selectedModel) update.preferredModel = selectedModel;
    if (prefs.webSearchMode !== webSearchMode) update.webSearchMode = webSearchMode;
    if (prefs.thinkingMode !== thinkingMode) update.thinkingMode = thinkingMode;
    if (prefs.autoSelectBest !== autoSelectBest) update.autoSelectBest = autoSelectBest;
    if (Object.keys(update).length > 0) updateChatPreferences(update);
  }, [selectedModel, webSearchMode, thinkingMode, autoSelectBest]);

  // Sync Supabase hydration (and cross-tab/preference-panel changes) into local state.
  // Thread-scoped values (contextMode/chatMode) are deliberately excluded — thread overrides win.
  useEffect(() => {
    return subscribeChatPreferences(() => {
      const prefs = getChatPreferencesSnapshot();
      const resolved = prefs.preferredModel ? resolveOpenRouterModelId(prefs.preferredModel) : "";
      if (resolved) setSelectedModel((prev) => (prev === resolved ? prev : resolved));
      setWebSearchMode((prev) => (prev === prefs.webSearchMode ? prev : prefs.webSearchMode));
      setThinkingMode((prev) => (prev === prefs.thinkingMode ? prev : prefs.thinkingMode));
      setAutoSelectBest((prev) => (prev === prefs.autoSelectBest ? prev : prefs.autoSelectBest));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore tools state from thread when thread changes
  useEffect(() => {
    if (!currentThreadId && lastLoadedToolsThreadIdRef.current !== null) {
      lastLoadedToolsThreadIdRef.current = null;
      const prefs = getChatPreferencesSnapshot();
      setContextMode(prefs.contextMode);
      setChatMode(prefs.chatMode);
      setSelectedGitHubRepos([]);
      return;
    }

    if (!currentThreadId || !currentThread) return;

    const threadChanged = lastLoadedToolsThreadIdRef.current !== currentThreadId;
    if (!threadChanged) return;

    const thread = currentThread as {
      contextMode?: string;
      chatMode?: string;
      linkedSpaceIds?: string[];
      attachedGitHubRepos?: SelectedGitHubRepo[];
      topicId?: string | null;
      agentId?: string | null;
    } | null;

    if (thread) {
      if (thread.contextMode) setContextMode(thread.contextMode as "MAX" | "LITE");
      if (thread.chatMode) setChatMode(thread.chatMode as "normal" | "questioning" | "dialogue");

      const threadSpaceIds = Array.isArray(thread.linkedSpaceIds) ? thread.linkedSpaceIds : [];
      setSelectedSpaceIds((prev) =>
        JSON.stringify(prev) !== JSON.stringify(threadSpaceIds) ? threadSpaceIds : prev
      );

      const threadRepos = Array.isArray(thread.attachedGitHubRepos) ? thread.attachedGitHubRepos : [];
      setSelectedGitHubRepos((prev) =>
        JSON.stringify(prev) !== JSON.stringify(threadRepos) ? threadRepos : prev
      );
      lastSavedGitHubReposRef.current = JSON.stringify(threadRepos);

      const threadTopicId = thread.topicId ?? null;
      setSelectedTopicId((prev) => (prev !== threadTopicId ? threadTopicId : prev));

      if (thread.agentId !== undefined) {
        setSelectedAgentId((prev) => (prev !== thread.agentId ? (thread.agentId ?? null) : prev));
        lastSavedAgentId.current = thread.agentId ?? null;
      }
    }

    lastLoadedToolsThreadIdRef.current = currentThreadId;
  }, [currentThreadId, currentThread]);

  useEffect(() => {
    if (pendingThreadUpdateTimerRef.current) {
      window.clearTimeout(pendingThreadUpdateTimerRef.current);
      pendingThreadUpdateTimerRef.current = null;
    }
    pendingThreadUpdateRef.current = {};
    pendingThreadIdRef.current = null;
    lastPersistedThreadSettingsRef.current = null;
  }, [currentThreadId]);

  useEffect(() => {
    const thread = currentThread as {
      contextMode?: string;
      chatMode?: string;
      agentId?: string | null;
      attachedGitHubRepos?: SelectedGitHubRepo[];
    } | null;
    if (!currentThreadId || !thread) return;

    const updates: Partial<{
      contextMode: string;
      chatMode: string;
      agentId?: string | null;
      attachedGitHubRepos?: SelectedGitHubRepo[];
    }> = {};
    const threadContextMode = thread.contextMode ?? "MAX";
    const threadChatMode = thread.chatMode ?? "normal";
    if (threadContextMode !== contextMode) updates.contextMode = contextMode;
    if (threadChatMode !== chatMode) updates.chatMode = chatMode;
    if (
      selectedAgentId !== thread.agentId &&
      selectedAgentId !== lastSavedAgentId.current
    ) {
      updates.agentId = selectedAgentId;
    }

    const threadRepos = Array.isArray(thread.attachedGitHubRepos) ? thread.attachedGitHubRepos : [];
    const reposKey = JSON.stringify(selectedGitHubRepos);
    const threadReposKey = JSON.stringify(threadRepos);
    if (reposKey !== threadReposKey && reposKey !== lastSavedGitHubReposRef.current) {
      updates.attachedGitHubRepos = selectedGitHubRepos;
    }

    if (Object.keys(updates).length === 0) return;
    const persistKey = JSON.stringify({
      id: currentThreadId,
      contextMode,
      chatMode,
      agentId: selectedAgentId ?? null,
      attachedGitHubRepos: selectedGitHubRepos,
    });
    if (lastPersistedThreadSettingsRef.current === persistKey) return;

    pendingThreadUpdateRef.current = {
      ...pendingThreadUpdateRef.current,
      ...updates,
    };
    pendingThreadIdRef.current = currentThreadId;

    if (pendingThreadUpdateTimerRef.current) {
      window.clearTimeout(pendingThreadUpdateTimerRef.current);
    }

    pendingThreadUpdateTimerRef.current = window.setTimeout(() => {
      const threadId = pendingThreadIdRef.current;
      const pending = pendingThreadUpdateRef.current;
      pendingThreadUpdateRef.current = {};
      pendingThreadUpdateTimerRef.current = null;

      if (!threadId || Object.keys(pending).length === 0) return;
      updateThreadMutation.mutate({ id: threadId, data: pending });
      lastPersistedThreadSettingsRef.current = JSON.stringify({
        id: threadId,
        contextMode,
        chatMode,
        agentId: selectedAgentId ?? null,
        attachedGitHubRepos: selectedGitHubRepos,
      });
      if ("agentId" in pending) {
        lastSavedAgentId.current = pending.agentId ?? null;
      }
      if ("attachedGitHubRepos" in pending) {
        lastSavedGitHubReposRef.current = JSON.stringify(pending.attachedGitHubRepos ?? []);
      }
    }, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMode, chatMode, selectedAgentId, selectedGitHubRepos, currentThreadId, currentThread]);

  const buildOnReady = useCallback(
    (fullName: string) =>
      (data: {
        info: NonNullable<Awaited<ReturnType<typeof githubService.getRepoAnalysisContext>>>['info'];
        tree: NonNullable<Awaited<ReturnType<typeof githubService.getRepoAnalysisContext>>>['tree'];
        readme: string | undefined;
      }) => {
        const context = {
          info: data.info,
          tree: data.tree,
          dependencies: {},
          languages: {} as Record<string, number>,
          readme: data.readme,
          allFiles: {} as Record<string, { content: string; size: number }>,
        };
        setCachedRepoContexts((prev) => ({ ...prev, [fullName]: context }));
        setIsLoadingRepoContext(false);
      },
    []
  );

  const runRepoIndexing = useCallback(
    async (repoOwner: string, repoName: string, repoFullName: string, forceFullReindex: boolean) => {
      const existing = indexingInFlightRef.current.get(repoFullName);
      if (existing && !forceFullReindex) return existing;
      if (existing && forceFullReindex) {
        indexingAbortRef.current.get(repoFullName)?.abort();
        indexingInFlightRef.current.delete(repoFullName);
      }

      const runPromise = (async () => {
        const onReady = buildOnReady(repoFullName);
        const targetId = `${repoOwner}/${repoName}`;

        indexingAbortRef.current.get(repoFullName)?.abort();
        const abortController = new AbortController();
        indexingAbortRef.current.set(repoFullName, abortController);

        setIsLoadingRepoContext(true);
        if (forceFullReindex) {
          maxProgressSeenRef.current.delete(repoFullName);
          console.log(`[Repo RAG] Refreshing index for ${repoFullName}`);
        }
        setRepoIndexingProgressMap((prev) => ({
          ...prev,
          [repoFullName]: { status: 'indexing', progress: 0 },
        }));

        const fetchLightweightContext = async () => {
          const [infoResult, treeResult, readmeResult] = await Promise.allSettled([
            githubService.getRepoInfo(repoOwner, repoName),
            githubService.getRepoTree(repoOwner, repoName),
            githubService.getReadme(repoOwner, repoName),
          ]);

          const failures: string[] = [];
          if (infoResult.status === 'rejected') {
            failures.push(`info: ${infoResult.reason instanceof Error ? infoResult.reason.message : String(infoResult.reason)}`);
          }
          if (treeResult.status === 'rejected') {
            failures.push(`tree: ${treeResult.reason instanceof Error ? treeResult.reason.message : String(treeResult.reason)}`);
          }
          if (readmeResult.status === 'rejected') {
            failures.push(`readme: ${readmeResult.reason instanceof Error ? readmeResult.reason.message : String(readmeResult.reason)}`);
          }
          if (failures.length > 0) {
            console.warn(
              `[Repo RAG] GitHub metadata partially unavailable for ${repoFullName}:`,
              failures.join('; ')
            );
          }

          if (infoResult.status === 'fulfilled' && treeResult.status === 'fulfilled') {
            const readme =
              readmeResult.status === 'fulfilled' && typeof readmeResult.value.content === 'string'
                ? readmeResult.value.content
                : undefined;
            onReady({ info: infoResult.value, tree: treeResult.value, readme });
          }
        };

        try {
          const indexStatus = await getRepoIndexStatus(repoOwner, repoName);

          if (!forceFullReindex && indexStatus.status === 'complete') {
            console.log(`[Repo RAG] Fully indexed — skipping job for ${repoFullName}`);

            const indexTreePaths = await getRepoTreePathsFromIndex(repoOwner, repoName);
            if (indexTreePaths.length > 0) {
              setCachedRepoContexts((prev) => ({
                ...prev,
                [repoFullName]: {
                  ...(prev[repoFullName] as object | undefined),
                  info: (prev[repoFullName] as { info?: { full_name?: string } } | undefined)?.info ?? {
                    full_name: repoFullName,
                  },
                  tree: {
                    tree: indexTreePaths.map((path) => ({ path, type: 'blob' as const })),
                  },
                  dependencies: {},
                  languages: {},
                  readme: (prev[repoFullName] as { readme?: string } | undefined)?.readme,
                  allFiles: {},
                },
              }));
              setIsLoadingRepoContext(false);
            }

            void fetchLightweightContext();
            const initialSymbolsStatus = indexStatus.symbolsStatus ?? null;
            setRepoIndexingProgressMap((prev) => ({
              ...prev,
              [repoFullName]: {
                status: 'done',
                progress: 100,
                indexStatus: 'complete',
                symbolsStatus: initialSymbolsStatus,
              },
            }));
            if (
              initialSymbolsStatus === 'pending' ||
              initialSymbolsStatus === 'processing'
            ) {
              const symbolsStatus = await embeddingJobService.waitForSymbolsComplete(
                targetId,
                (symStatus) => {
                  if (abortController.signal.aborted) return;
                  setRepoIndexingProgressMap((prev) => ({
                    ...prev,
                    [repoFullName]: {
                      ...(prev[repoFullName] ?? {
                        status: 'done',
                        progress: 100,
                        indexStatus: 'complete',
                      }),
                      symbolsStatus: symStatus,
                    },
                  }));
                },
                { pollMs: 2500, maxWaitMs: 3_600_000, signal: abortController.signal }
              );
              setRepoIndexingProgressMap((prev) => ({
                ...prev,
                [repoFullName]: {
                  status: 'done',
                  progress: 100,
                  indexStatus: 'complete',
                  symbolsStatus,
                },
              }));
              if (symbolsStatus === 'completed') {
                console.log(`[Repo RAG] Symbols completed for ${repoFullName}`);
              } else if (symbolsStatus === 'failed') {
                console.warn(`[Repo RAG] Symbols failed for ${repoFullName}`);
              }
            }
            return;
          }

          if (indexStatus.status === 'pass1Only' || indexStatus.status === 'partial') {
            console.log(
              `[Repo RAG] Resuming index for ${repoFullName} (${indexStatus.status}, ${indexStatus.codeChunks} code chunks)`
            );
          }

          void fetchLightweightContext();

          if (abortController.signal.aborted) return;

          await embeddingJobService.enqueueRepoIndex(repoOwner, repoName, {
            forceFullReindex: forceFullReindex,
          });

          if (abortController.signal.aborted) return;

          let lastSymbolsStatus:
            | 'pending'
            | 'processing'
            | 'completed'
            | 'failed'
            | 'skipped'
            | null = null;

          await embeddingJobService.waitForRepoJob(
            targetId,
            (view, job) => {
              if (abortController.signal.aborted) return;
              const maxSeen = Math.max(
                maxProgressSeenRef.current.get(repoFullName) ?? 0,
                view.progress ?? 0
              );
              maxProgressSeenRef.current.set(repoFullName, maxSeen);
              lastSymbolsStatus = view.symbolsStatus ?? job.symbols_status ?? null;
              setRepoIndexingProgressMap((prev) => ({
                ...prev,
                [repoFullName]: {
                  status: view.status,
                  progress: maxSeen,
                  batchIndex: view.batchIndex,
                  totalBatches: view.totalBatches,
                  chunksIndexed: view.chunksIndexed,
                  errorMessage: view.errorMessage,
                  phase: view.phase,
                  lastHeartbeat: view.lastHeartbeat,
                  indexStatus: deriveLiveRepoIndexStatus(view),
                  symbolsStatus: lastSymbolsStatus,
                },
              }));
            },
            { pollMs: 2000, maxWaitMs: 3_600_000, signal: abortController.signal }
          );

          if (abortController.signal.aborted) return;

          const postEmbedStatus = await getRepoIndexStatus(repoOwner, repoName);
          let symbolsStatus =
            lastSymbolsStatus ?? postEmbedStatus.symbolsStatus ?? 'pending';

          setRepoIndexingProgressMap((prev) => ({
            ...prev,
            [repoFullName]: {
              status: 'done',
              progress: 100,
              indexStatus: 'complete',
              symbolsStatus,
            },
          }));
          maxProgressSeenRef.current.set(repoFullName, 100);
          console.log(`[Repo RAG] Server indexing completed for ${repoFullName}`);

          if (symbolsStatus === 'pending' || symbolsStatus === 'processing') {
            symbolsStatus = await embeddingJobService.waitForSymbolsComplete(
              targetId,
              (symStatus) => {
                if (abortController.signal.aborted) return;
                setRepoIndexingProgressMap((prev) => ({
                  ...prev,
                  [repoFullName]: {
                    ...(prev[repoFullName] ?? {
                      status: 'done',
                      progress: 100,
                      indexStatus: 'complete',
                    }),
                    symbolsStatus: symStatus,
                  },
                }));
              },
              { pollMs: 2500, maxWaitMs: 3_600_000, signal: abortController.signal }
            );
            setRepoIndexingProgressMap((prev) => ({
              ...prev,
              [repoFullName]: {
                status: 'done',
                progress: 100,
                indexStatus: 'complete',
                symbolsStatus,
              },
            }));
            if (symbolsStatus === 'completed') {
              console.log(`[Repo RAG] Symbols completed for ${repoFullName}`);
            } else if (symbolsStatus === 'failed') {
              console.warn(`[Repo RAG] Symbols failed for ${repoFullName}`);
            }
          }
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            return;
          }
          const message =
            err instanceof Error && err.message.trim()
              ? err.message
              : 'Repo indexing failed (unknown error)';
          console.warn('[Repo RAG] Indexing failed (search will use repo summary only):', err);
          lastFetchedReposRef.current.delete(repoFullName);
          setRepoIndexingProgressMap((prev) => ({
            ...prev,
            [repoFullName]: { status: 'error', progress: 0, errorMessage: message },
          }));
        } finally {
          if (indexingAbortRef.current.get(repoFullName) === abortController) {
            indexingAbortRef.current.delete(repoFullName);
          }
          setIsLoadingRepoContext(false);
        }
      })();

      indexingInFlightRef.current.set(repoFullName, runPromise);
      try {
        await runPromise;
        lastFetchedReposRef.current.add(repoFullName);
      } finally {
        indexingInFlightRef.current.delete(repoFullName);
      }
    },
    [buildOnReady]
  );

  const refreshRepoIndexForSelection = useCallback(async () => {
    indexingAbortRef.current.forEach((ac) => ac.abort());
    indexingAbortRef.current.clear();
    indexingInFlightRef.current.clear();
    embeddingJobService.resetProcessInFlight();

    for (const repo of selectedGitHubRepos) {
      lastFetchedReposRef.current.delete(repo.fullName);
      maxProgressSeenRef.current.delete(repo.fullName);
    }

    await Promise.allSettled(
      selectedGitHubRepos.map((repo) =>
        runRepoIndexing(repo.owner, repo.name, repo.fullName, true)
      )
    );
  }, [selectedGitHubRepos, runRepoIndexing]);

  const patchCachedRepoFileContent = useCallback(
    (
      fullName: string,
      files: Array<{ file_path: string; content: string }>
    ) => {
      if (files.length === 0) return;
      setCachedRepoContexts((prev) => {
        const existing = prev[fullName];
        if (!existing) return prev;
        const allFiles = { ...(existing.allFiles ?? {}) };
        for (const f of files) {
          allFiles[f.file_path] = {
            content: f.content,
            size: new Blob([f.content]).size,
          };
        }
        return { ...prev, [fullName]: { ...existing, allFiles } };
      });
    },
    []
  );

  const toggleGitHubRepo = useCallback((repo: SelectedGitHubRepo) => {
    setSelectedGitHubRepos((prev) => {
      const exists = prev.some((r) => r.fullName === repo.fullName);
      if (exists) return prev.filter((r) => r.fullName !== repo.fullName);
      if (prev.length >= MAX_ATTACHED_REPOS) return prev;
      return [...prev, repo];
    });
  }, []);

  // Index newly attached repos
  useEffect(() => {
    if (selectedGitHubRepos.length === 0) {
      setCachedRepoContexts({});
      setIsLoadingRepoContext(false);
      setRepoIndexingProgressMap({});
      maxProgressSeenRef.current.clear();
      lastFetchedReposRef.current = new Set();
      indexingInFlightRef.current.clear();
      indexingAbortRef.current.forEach((ac) => ac.abort());
      indexingAbortRef.current.clear();
      return;
    }

    void (async () => {
      const toIndex = selectedGitHubRepos.filter(
        (repo) =>
          !lastFetchedReposRef.current.has(repo.fullName) &&
          !indexingInFlightRef.current.has(repo.fullName)
      );
      await Promise.allSettled(
        toIndex.map((repo) =>
          runRepoIndexing(repo.owner, repo.name, repo.fullName, false)
        )
      );
    })();

    const active = new Set(selectedGitHubRepos.map((r) => r.fullName));
    for (const name of [...lastFetchedReposRef.current]) {
      if (!active.has(name)) {
        lastFetchedReposRef.current.delete(name);
        indexingAbortRef.current.get(name)?.abort();
        indexingAbortRef.current.delete(name);
      }
    }
    setCachedRepoContexts((prev) => {
      const next: typeof prev = {};
      for (const key of Object.keys(prev)) {
        if (active.has(key)) next[key] = prev[key];
      }
      return next;
    });
  }, [selectedReposKey, runRepoIndexing, selectedGitHubRepos]);

  return {
    modelSelectorOpen,
    setModelSelectorOpen,
    toolsMenuOpen,
    setToolsMenuOpen,
    selectedModel,
    setSelectedModel,
    modelInfo,
    setModelInfo,
    selectedAgentId,
    setSelectedAgentId,
    selectedSuperPrompt,
    setSelectedSuperPrompt,
    selectedWorkflow,
    setSelectedWorkflow,
    webSearchMode,
    setWebSearchMode,
    thinkingMode,
    setThinkingMode,
    autoSelectBest,
    setAutoSelectBest,
    deepResearchMode,
    setDeepResearchMode,
    contextMode,
    setContextMode,
    chatMode,
    setChatMode,
    selectedSpaceIds,
    setSelectedSpaceIds,
    selectedTopicId,
    setSelectedTopicId,
    useFullScope,
    setUseFullScope,
    selectedSourceIds,
    setSelectedSourceIds,
    selectedDocumentIds,
    setSelectedDocumentIds,
    selectedGitHubRepo,
    setSelectedGitHubRepo,
    selectedGitHubRepos,
    setSelectedGitHubRepos,
    toggleGitHubRepo,
    maxAttachedRepos: MAX_ATTACHED_REPOS,
    cachedRepoContext,
    cachedRepoContexts,
    cachedRepoContextRef,
    isLoadingRepoContext,
    repoIndexingProgress,
    repoIndexingProgressMap,
    refreshRepoIndexForSelection,
    patchCachedRepoFileContent,
    availableModels,
    refetchModels,
    isRefreshingModels,
    isLoadingModels,
    isModelsFetchError,
    modelsFetchError,
    isModelsFetched,
    isModelsReady: isModelsFetched && !isModelsFetchError,
    modelsLastUpdated,
  };
}
